import { useCallback, useRef, useState } from 'react'

import axios from '../../dataClient/dataClient'
import { serializeBoxes, textUnderBox } from '../../util/redaction/annotationMath'
import { EDITABLE_CATEGORY_STATUSES, EDIT_REDACTIONS_SKIP_CODES, REDACTION_OCCURRENCE_ACTION } from 'citadel-shared/redactionEditing.js'
import { redactAllSummary } from './redactAllSummary'
import { WorkflowPollTimeoutError, WorkflowPollCancelledError } from './useWorkflowPoll'

function sumOcrSkippedPages(outcomes) {
  return outcomes.reduce((total, outcome) => total + (outcome.workflow?.summary?.ocrSkippedPageCount ?? 0), 0)
}

function buildOccurrenceFailureMessage(action, detail) {
  const actionLabel = action === REDACTION_OCCURRENCE_ACTION.UNREDACT ? 'Un-redacting' : 'Redacting'
  return `${actionLabel} all occurrences failed${detail ? `: ${detail}` : ''}. Try again.`
}

// Manage the redact-all dialog, workflows, reload, and summary state.
export default function useRedactAllOccurrences({
  foiId,
  categoryId,
  pages,
  boxes,
  selected,
  dirty,
  busy,
  baseVersion,
  maxBoxes,
  poll,
  reloadFile,
  onStaleVersion
}) {
  const [open, setOpen] = useState(false)
  const [action, setAction] = useState(REDACTION_OCCURRENCE_ACTION.REDACT)
  const [prefill, setPrefill] = useState({ text: '', reason: '' })
  const [requestScope, setRequestScope] = useState(null)
  const [redacting, setRedacting] = useState(false)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)
  const pendingRef = useRef(null)

  const openDialog = useCallback(
    async (nextAction = REDACTION_OCCURRENCE_ACTION.REDACT) => {
      if (dirty || busy) return
      let text = ''
      if (selected) {
        try {
          const page = pages[selected.page]
          const content = await page.getTextContent()
          text = textUnderBox(content, page.getViewport({ scale: 1 }), selected)
        } catch (e) {
          console.error('useRedactAllOccurrences: could not read the text under the selected box', e)
        }
      }
      setPrefill({ text, reason: selected?.reason ?? '' })
      setAction(nextAction)
      setError(null)
      setOpen(true)
      setRequestScope(null)
      try {
        const { data } = await axios.get(`/category?foiId=${foiId}`)
        const categories = data.data.categories ?? []
        setRequestScope({ inScope: categories.filter(c => EDITABLE_CATEGORY_STATUSES.includes(c.status)).length, total: categories.length })
      } catch (e) {
        console.error('useRedactAllOccurrences: could not count the request categories', e)
      }
    },
    [dirty, busy, selected, pages, foiId]
  )

  const closeDialog = useCallback(() => {
    if (!redacting) setOpen(false)
  }, [redacting])

  const confirm = useCallback(
    async input => {
      if (dirty || busy) return
      setRedacting(true)
      setError(null)
      setSummary(null)
      try {
        const { data } = await axios.post(`/category/${categoryId}/redactions/occurrences`, { ...input, baseVersion })
        const response = data.data
        const outcomes = await Promise.all(response.started.map(async entry => ({ ...entry, workflow: await poll(entry.workflowId) })))
        const failed = outcomes.filter(o => o.workflow.status !== 'SUCCEEDED').map(o => ({ ...o, errorMessage: o.workflow.errorMessage }))
        const thisFile = outcomes.find(o => o.categoryId === categoryId)
        if (thisFile && thisFile.workflow.status !== 'SUCCEEDED') {
          setError(buildOccurrenceFailureMessage(input.action, thisFile.workflow.errorMessage))
          if (response.scope === 'request')
            setSummary(
              redactAllSummary({
                added: 0,
                removed: 0,
                action: input.action,
                response,
                categoryId,
                failed,
                ocrSkippedPages: sumOcrSkippedPages(outcomes)
              })
            )
          return
        }
        setOpen(false)
        if (thisFile) {
          const selectedGeometry = selected ? { page: selected.page, x: selected.x, y: selected.y, w: selected.w, h: selected.h } : null
          pendingRef.current = {
            before: boxes.length,
            beforeSnapshot: serializeBoxes(boxes),
            action: input.action,
            response,
            failed,
            selectedGeometry,
            truncated: Boolean(thisFile.workflow.summary?.truncated),
            ocrAdded: thisFile.workflow.summary?.ocrAddedCount ?? 0,
            removed: thisFile.workflow.summary?.removedCount,
            ocrSkippedPages: sumOcrSkippedPages(outcomes)
          }
          await reloadFile()
        } else {
          setSummary(
            redactAllSummary({
              added: 0,
              removed: 0,
              action: input.action,
              response,
              categoryId,
              failed,
              ocrSkippedPages: sumOcrSkippedPages(outcomes)
            })
          )
        }
      } catch (e) {
        if (e instanceof WorkflowPollCancelledError) return
        if (e.response?.status === 409 && e.response.data?.code === EDIT_REDACTIONS_SKIP_CODES.STALE_VERSION) {
          console.info('useRedactAllOccurrences: the noted file changed since it was loaded (409 STALE_VERSION)')
          setOpen(false)
          onStaleVersion?.()
          return
        }
        console.error('useRedactAllOccurrences: redact all occurrences failed', e)
        const message = e instanceof WorkflowPollTimeoutError ? 'the server did not finish in time' : e.response?.data?.message
        setError(buildOccurrenceFailureMessage(input.action, message))
      } finally {
        setRedacting(false)
      }
    },
    [dirty, busy, categoryId, baseVersion, poll, selected, boxes.length, reloadFile, onStaleVersion]
  )

  const consumeReload = useCallback(
    loadedBoxes => {
      if (!pendingRef.current) return null
      const {
        before,
        beforeSnapshot,
        action: completedAction,
        response,
        failed,
        selectedGeometry,
        truncated,
        ocrAdded,
        removed,
        ocrSkippedPages
      } = pendingRef.current
      pendingRef.current = null
      const changed = serializeBoxes(loadedBoxes) !== beforeSnapshot
      const inferredRemoved = Math.max(before - loadedBoxes.length, 0)
      setSummary(
        redactAllSummary({
          added: Math.max(loadedBoxes.length - before, 0),
          removed: removed ?? (inferredRemoved || null),
          changed,
          action: completedAction,
          response,
          categoryId,
          failed,
          truncated,
          maxBoxes,
          ocrAdded,
          ocrSkippedPages
        })
      )
      return selectedGeometry
    },
    [categoryId, maxBoxes]
  )

  return {
    isOpen: open,
    action,
    prefill,
    requestScope,
    redacting,
    error,
    summary,
    dismissSummary: () => setSummary(null),
    openRedactDialog: () => openDialog(REDACTION_OCCURRENCE_ACTION.REDACT),
    openUnredactDialog: () => openDialog(REDACTION_OCCURRENCE_ACTION.UNREDACT),
    closeDialog,
    confirm,
    consumeReload
  }
}
